import { computed } from "@vue/composition-api";
import { ActionTree } from "vuex";
import { User } from "@scrile/api-provider/dist/api/UserProvider";
import { UserVerificationStatus } from "@scrile/api-provider/dist/api/UserVerificationProvider";
import { CREDITS_CURRENCY_CODE, setCreditCurrencyEqlCode } from "@scrile/tools/dist/lib/MoneyHelpers";
import { ACTION_TYPES, MUTATION_TYPES } from "@/store/const";
import providers, { getManagerProviders } from "@/lib/api";
import { AppSessionStorage } from "@/lib/appStorage";
import { LoginData } from "@/lib/authHelper";
import payments from "@/payments";
import { STORAGE } from "@/const";
import { StateRoot } from "./state";

export const actions: ActionTree<StateRoot, StateRoot> = {
  async [ACTION_TYPES.LOGIN](context, data: LoginData) {
    context.commit(MUTATION_TYPES.SET_LOGIN_DATA, data);
    const promise = Promise.all([
      context.dispatch(ACTION_TYPES.GET_VERIFICATION_STATUS, data.user),
      context.dispatch(ACTION_TYPES.GET_NSFW_USER_SETTING, data.user),
      context.dispatch(ACTION_TYPES.GET_ONBOARDING, data.user),
    ]);
    context.commit(MUTATION_TYPES.SET_LOGIN_PROCESSING, promise);
    await promise;
    context.commit(MUTATION_TYPES.SET_LOGIN_PROCESSING, undefined);
  },
  async [ACTION_TYPES.LOGOUT](context) {
    await providers.LoginProvider.logout();
    await getManagerProviders().providers.LoginProvider.setLongLiveToken("");
    context.commit(MUTATION_TYPES.LOGOUT);
  },
  async [ACTION_TYPES.GET_VERIFICATION_STATUS](context, user: User) {
    const userVerification = await providers.UserVerificationProvider.search({ data: { userIds: [user.id] } });
    let dataVerified = userVerification.result.find(i => i.userId === user.id);
    context.commit(
      MUTATION_TYPES.SET_USER_VERIFICATION_STATUS,
      dataVerified?.status ?? UserVerificationStatus.UNVERIFIED
    );
  },
  async [ACTION_TYPES.GET_SYSTEM_SETTINGS](context) {
    const [systemSettings, publicPaymentSystems] = await Promise.all([
      providers.SystemSettingsProvider.get(),
      providers.PaymentSystemsProvider.findAllPublic(),
    ]);
    const config = computed(() =>
      typeof systemSettings.frontendConfig === "string" ? JSON.parse(systemSettings.frontendConfig) : {}
    );
    setCreditCurrencyEqlCode(config.value?.showCreditIcon ? CREDITS_CURRENCY_CODE : systemSettings.mainCurrency);
    context.commit(MUTATION_TYPES.SET_STATE, {
      systemSettings,
      publicPaymentSystems,
      creditCardAllowed: publicPaymentSystems.some(system => payments.checkAllowStoreCard(system)),
    });
    return systemSettings;
  },
  async [ACTION_TYPES.GET_NSFW_USER_SETTING](context, user: User) {
    const response = await providers.NSFWSettingsProvider.userSettingsFind({ userId: user.id });
    context.commit(MUTATION_TYPES.SET_NSFW_USER_SETTING, response?.showNotSafeForWork);
  },
  async [ACTION_TYPES.GET_ONBOARDING](context, user: User) {
    const response = await providers.UserFrontendData.search({ userId: user.id });
    const stored = AppSessionStorage.getObject(STORAGE.ONBOARDING, {});
    const data = {
      ...stored,
      ...(response?.onboarding ?? {}),
    };
    context.commit(MUTATION_TYPES.SET_ONBOARDING, data);
  },
};
